import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowRight,
  Camera,
  CalendarDays,
  CheckSquare,
  Eye,
  EyeOff,
  LoaderCircle,
} from "lucide-react";
import campus from "../assets/login-campus.jpg";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage("");
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success("Login successful", {
        description: `Welcome back, ${user.name}!`,
      });

      // Redirect based on role
      if (user.role === "team_member") {
        navigate("/my-tasks");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.detail ||
          "Unable to sign in. Check your email and password and try again.",
      );
      toast.error("Login failed", {
        description:
          error.response?.data?.detail || "Invalid email or password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-hero" aria-label="SMV Media">
        <img
          className="login-hero-image"
          src={campus}
          alt=""
          fetchPriority="high"
        />
        <div className="login-hero-copy">
          <div className="login-brand">
            <img
              src={`${process.env.PUBLIC_URL}/logos/smv-media-login.png`}
              alt=""
              width="40"
              height="40"
            />
            <span>SMV Media</span>
          </div>
          <div className="login-hero-intro">

            <p className="login-tagline">
              <span>Plan. Capture.</span>
              <span>Deliver.</span>
            </p>

            <ul className="login-hero-points" aria-label="What you can do">
              <li>
                <CalendarDays size={15} aria-hidden="true" />
                Plan events
              </li>
              <li>
                <CheckSquare size={15} aria-hidden="true" />
                Assign tasks
              </li>
              <li>
                <Camera size={15} aria-hidden="true" />
                Deliver media
              </li>
            </ul>
          </div>
        </div>
      </aside>

      <main className="login-panel">
        <div className="login-card">
          <div className="login-brand login-brand-compact">
            <img
              src={`${process.env.PUBLIC_URL}/logos/smv-media-login.png`}
              alt=""
              width="36"
              height="36"
            />
            <span>SMV Media</span>
          </div>

          <div className="login-heading">
            <h1>Welcome back!</h1>
            <p>Sign in to manage your media events.</p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="login-form"
            aria-busy={loading}
          >
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                className="field-input"
                placeholder="you@smvec.ac.in"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="password">Password</label>
              <div className="password-field">
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  className="field-input"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {errorMessage && (
              <p className="login-error" role="alert">
                {errorMessage}
              </p>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              className="login-submit"
              disabled={loading}
            >
              {loading && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                  aria-hidden="true"
                />
              )}
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="login-note">
            Need access? <span>Contact your administrator.</span>
          </p>
        </div>

        <Link to="/deliveries" className="login-gallery-link">
          Public gallery <ArrowRight size={15} />
        </Link>
      </main>
    </div>
  );
};

export default Login;
